/**
 * theme-tracking.js — Meta Pixel + Google Ads e-commerce event tracking
 * Fires on: ViewContent, AddToCart, InitiateCheckout, Purchase
 *
 * Events are only sent when the corresponding tracking ID is configured
 * in theme settings (Tracking & Analytics).
 */

(function () {
  'use strict';

  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */
  const formatPrice = (cents) => (cents / 100).toFixed(2);

  const getProductData = () => {
    const script = $('[data-product-json]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch { return null; }
  };

  const getSelectedVariant = () => {
    const el = $('[data-selected-variant-id]');
    if (!el) return null;
    const id = parseInt(el.dataset.selectedVariantId, 10);
    const product = getProductData();
    if (!product || !product.variants) return null;
    return product.variants.find((v) => v.id === id) || product.variants[0];
  };

  /* ------------------------------------------------------------------
     Meta Pixel events
     ------------------------------------------------------------------ */
  const metaEvent = (eventName, params = {}) => {
    if (typeof fbq !== 'function') return;
    fbq('track', eventName, params);
  };

  const metaEventCustom = (eventName, params = {}) => {
    if (typeof fbq !== 'function') return;
    fbq('trackCustom', eventName, params);
  };

  /* ------------------------------------------------------------------
     Google Ads events
     ------------------------------------------------------------------ */
  const gtagEvent = (eventName, params = {}) => {
    if (typeof gtag !== 'function') return;
    gtag('event', eventName, params);
  };

  /* ------------------------------------------------------------------
     ViewContent — Product page
     ------------------------------------------------------------------ */
  const fireViewContent = () => {
    const product = getProductData();
    if (!product) return;
    const variant = getSelectedVariant() || product.variants[0];
    const params = {
      content_ids: [variant ? variant.id : product.id],
      content_type: 'product',
      content_name: product.title,
      currency: window.Shopify?.currency?.active || 'INR',
      value: variant ? formatPrice(variant.price) : '0.00',
    };
    metaEvent('ViewContent', params);
    gtagEvent('view_item', {
      currency: params.currency,
      value: parseFloat(params.value),
      items: [{
        item_id: String(params.content_ids[0]),
        item_name: product.title,
        price: parseFloat(params.value),
        quantity: 1,
      }],
    });
  };

  /* ------------------------------------------------------------------
     AddToCart
     ------------------------------------------------------------------ */
  const fireAddToCart = (variant, qty = 1) => {
    if (!variant) return;
    const params = {
      content_ids: [variant.id],
      content_type: 'product',
      content_name: variant.name || variant.title,
      currency: window.Shopify?.currency?.active || 'INR',
      value: formatPrice(variant.price * qty),
      quantity: qty,
    };
    metaEvent('AddToCart', params);
    gtagEvent('add_to_cart', {
      currency: params.currency,
      value: parseFloat(params.value),
      items: [{
        item_id: String(variant.id),
        item_name: variant.name || variant.title,
        price: formatPrice(variant.price),
        quantity: qty,
      }],
    });
  };

  /* ------------------------------------------------------------------
     InitiateCheckout
     ------------------------------------------------------------------ */
  const fireInitiateCheckout = () => {
    const items = window.__leosportz_checkout_items || [];
    if (!items.length) return;
    const value = items.reduce((sum, it) => sum + (it.price * it.quantity), 0);
    const params = {
      content_ids: items.map((it) => it.variant_id),
      content_type: 'product',
      currency: window.Shopify?.currency?.active || 'INR',
      value: formatPrice(value),
      num_items: items.reduce((sum, it) => sum + it.quantity, 0),
    };
    metaEvent('InitiateCheckout', params);
    gtagEvent('begin_checkout', {
      currency: params.currency,
      value: parseFloat(params.value),
      items: items.map((it) => ({
        item_id: String(it.variant_id),
        item_name: it.title,
        price: formatPrice(it.price),
        quantity: it.quantity,
      })),
    });
  };

  /* ------------------------------------------------------------------
     Purchase (fires on order status page)
     ------------------------------------------------------------------ */
  const firePurchase = () => {
    const el = $('[data-order-id]');
    if (!el) return;
    const orderId = el.dataset.orderId;
    const total = parseFloat(el.dataset.orderTotal || '0');
    const currency = el.dataset.orderCurrency || 'INR';
    const items = window.__leosportz_order_items || [];

    metaEvent('Purchase', {
      content_ids: items.map((it) => it.variant_id),
      content_type: 'product',
      currency,
      value: total.toFixed(2),
      num_items: items.reduce((sum, it) => sum + it.quantity, 0),
    });

    gtagEvent('purchase', {
      transaction_id: orderId,
      currency,
      value: total,
      items: items.map((it) => ({
        item_id: String(it.variant_id),
        item_name: it.title,
        price: formatPrice(it.price),
        quantity: it.quantity,
      })),
    });
  };

  /* ------------------------------------------------------------------
     Wire up listeners
     ------------------------------------------------------------------ */
  const init = () => {
    /* ViewContent on product pages */
    if ($('[data-product-json]')) {
      fireViewContent();
      document.addEventListener('variant:changed', () => fireViewContent());
    }

    /* AddToCart — main product form */
    const atcForm = $('#product-form, [data-product-form]');
    if (atcForm) {
      atcForm.addEventListener('submit', (e) => {
        const variant = getSelectedVariant();
        const qtyEl = $('.qty-selector__input, [data-quantity-input]');
        const qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
        fireAddToCart(variant, qty);
      });
    }

    /* AddToCart — quick-add buttons on collection grids */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      const variantId = parseInt(btn.dataset.quickAdd, 10);
      if (!variantId) return;
      fireAddToCart({ id: variantId, price: 0, name: btn.closest('.product-card')?.querySelector('.product-card__title')?.textContent?.trim() || '' }, 1);
    });

    /* InitiateCheckout — cart drawer / cart page checkout button */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('a[href="/checkout"], [data-checkout-button]');
      if (!btn) return;
      fireInitiateCheckout();
    });

    /* Purchase — order status page */
    if ($('[data-order-id]')) {
      firePurchase();
    }

    /* Search */
    const searchForm = $('form[action="/search"]');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        const input = searchForm.querySelector('input[name="q"]');
        if (input && input.value.trim()) {
          metaEventCustom('Search', { search_string: input.value.trim() });
          gtagEvent('search', { search_term: input.value.trim() });
        }
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
